import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GraphView } from 'reactjs-interactive-graph';

import Navbar from '../components/Navbar/Navbar';
import Loading from '../components/Loading/Loading';
import Timeline from '../components/Timeline/Timeline';

import { fetchTransactionsForAddress } from '../api/adapters';
import { normalizeTxArray } from '../api/lib/normalize';
import { processTransactions } from '../api/lib/graph/processTransactions';
import useWindowWidth from '../utils/useWindowWidth';
import { toFriendlyTon } from '../utils/tonFriendly';

const DOUBLE_CLICK_DELAY = 250;

// ── Convert raw address to friendly if TON/TON Testnet ─────────
function toFriendly(addr, network) {
    if (!addr) {
        return addr;
    }
    if (network === 'TON' || network === 'TON Testnet') {
        try {
            return toFriendlyTon(addr, { testOnly: network === 'TON Testnet', bounceable: false });
        } catch {
            return addr;
        }
    }
    return addr;
}

// ── Parse annotations from URL query string ─────────────
function parseAnnotationsFromUrl(search) {
    const p = new URLSearchParams(search);
    const map = new Map();
    const anns    = p.getAll('ann');
    const colors  = p.getAll('acolor');
    const notes   = p.getAll('anote');
    for (let i = 0; i < anns.length; i++) {
        const addr = anns[i];
        const color = colors[i] || '';
        const note  = notes[i] || '';
        if (addr && (color || note)) {
            map.set(addr, { color: color || null, note: note || '' });
        }
    }
    return map;
}

// ── Build full URL query string (addresses + annotations) ─
function buildUrlQs(addressPairs, annotations) {
    const parts = [];
    for (const { address, network } of addressPairs) {
        parts.push(`network=${encodeURIComponent(network)}&address=${encodeURIComponent(address)}`);
    }
    for (const [addr, ann] of annotations) {
        if (ann.color || ann.note) {
            parts.push(
                `ann=${encodeURIComponent(addr)}` +
                `&acolor=${encodeURIComponent(ann.color || '')}` +
                `&anote=${encodeURIComponent(ann.note || '')}`,
            );
        }
    }
    return parts.join('&');
}

// ── Read current address pairs from URL ─────────────────
function getAddressPairsFromUrl() {
    const p = new URLSearchParams(window.location.search);
    const addrs    = p.getAll('address');
    const networks = p.getAll('network');
    return addrs.map((a, i) => ({ address: a, network: networks[i] || 'Ethereum' }));
}

// ── Heatmap color ramp: blue → green → yellow → red ─────
function heatColor(ratio) {
    const r = ratio;
    let R, G, B;
    if (r < 0.25) {
        const t = r / 0.25;
        R = 0;
        G = Math.round(t * 180);
        B = Math.round(200 - t * 100);
    } else if (r < 0.5) {
        const t = (r - 0.25) / 0.25;
        R = 0;
        G = Math.round(180 + t * 75);
        B = Math.round(100 - t * 100);
    } else if (r < 0.75) {
        const t = (r - 0.5) / 0.25;
        R = Math.round(t * 255);
        G = 255;
        B = 0;
    } else {
        const t = (r - 0.75) / 0.25;
        R = 255;
        G = Math.round(255 - t * 200);
        B = 0;
    }
    const hex = (v) => v.toString(16).padStart(2, '0');
    return `#${hex(R)}${hex(G)}${hex(B)}`;
}

export default function ScanResult() {
    const location = useLocation();

    // ── Parse initial addresses from URL ────────────────────
    const initial = useMemo(() => {
        const p = new URLSearchParams(location.search);
        const addrs    = p.getAll('address');
        const networks = p.getAll('network');
        return addrs.map((a, i) => ({
            address: a,
            network: networks[i] || 'Ethereum',
        }));
    }, [location.search]);

    const graphRef     = useRef(null);
    const clickTimer   = useRef(new Map());
    const graphWrapRef = useRef(null);

    const [selectedAddress, setSelectedAddress] = useState(null);
    const [isModalOpen,     setIsModalOpen]     = useState(false);
    const [isGraphLoading,  setIsGraphLoading]  = useState(false);
    const [loading,         setLoading]         = useState(true);
    const [error,           setError]           = useState(null);

    // ── Tooltip state ───────────────────────────────────────
    const [tooltip, setTooltip] = useState(null);

    // ── Heatmap mode ────────────────────────────────────────
    const [heatmapOn, setHeatmapOn] = useState(false);

    const loadedAddrs = useRef(new Set());

    const [allTxBuckets, setAllTxBuckets] = useState([]);
    const allTxBucketsRef = useRef([]);

    const originalColors = useRef(new Map());

    // ── Node annotations: Map<nodeId, { color?, note? }> ────
    const [nodeAnnotations, setNodeAnnotations] = useState(() => parseAnnotationsFromUrl(location.search));
    const nodeAnnotationsRef = useRef(nodeAnnotations);

    // ── Floating note label DOM pool (no React re-renders) ──
    const noteLabelPoolRef = useRef(new Map());  // nodeId → HTMLDivElement
    const noteOverlayRef = useRef(null);

    useEffect(() => {
        nodeAnnotationsRef.current = nodeAnnotations;
    }, [nodeAnnotations]);

    useEffect(() => {
        allTxBucketsRef.current = allTxBuckets;
    }, [allTxBuckets]);

    const width = useWindowWidth();

    // ── Label formatter ─────────────────────────────────────
    const labelFormatter = useCallback(
        (addr, net) => {
            let s =
                net === 'TON' || net === 'TON Testnet'
                    ? toFriendlyTon(addr, { testOnly: net === 'TON Testnet', bounceable: true })
                    : addr;
            const keep = width < 1280 ? 6 : 10;
            if (s.length > 2 * keep) {
                return `${s.slice(0, keep)}…${s.slice(-keep)}`;
            }
            return s;
        },
        [width],
    );

    // ── Update floating note labels (direct DOM, no re-renders) ─
    const updateNoteLabels = useCallback(() => {
        const gv = graphRef.current;
        const cosmos = gv?.cosmos;
        const overlay = noteOverlayRef.current;
        if (!cosmos || !overlay) {
            return;
        }

        const tracked = cosmos.getTrackedPointPositionsMap();
        const pool = noteLabelPoolRef.current;
        const seen = new Set();

        tracked.forEach((pos, pointIndex) => {
            if (!pos) {
                return;
            }
            const nodeId = gv.getIdByIndex(pointIndex);
            if (!nodeId) {
                return;
            }
            const ann = nodeAnnotationsRef.current.get(nodeId);
            if (!ann?.note) {
                return;
            }

            seen.add(nodeId);

            const [sx, sy] = cosmos.spaceToScreenPosition(pos);
            const r = cosmos.spaceToScreenRadius(cosmos.getPointRadiusByIndex(pointIndex));

            let el = pool.get(nodeId);
            if (!el) {
                el = document.createElement('div');
                el.className = 'note-label';
                overlay.appendChild(el);
                pool.set(nodeId, el);
            }

            el.textContent = ann.note;
            el.style.left = sx + 'px';
            el.style.top = (sy - r - 6) + 'px';
        });

        // Remove stale labels
        for (const [id, el] of pool) {
            if (!seen.has(id)) {
                el.remove();
                pool.delete(id);
            }
        }
    }, []);

    // ── Re-track annotated nodes ────────────────────────────
    const retrackAnnotatedNodes = useCallback(() => {
        const gv = graphRef.current;
        if (!gv?.cosmos) {
            return;
        }

        const annotatedIds = [];
        for (const [id, ann] of nodeAnnotationsRef.current) {
            if (ann.note) {
                annotatedIds.push(id);
            }
        }

        const indices = gv.idsToIndices(annotatedIds);
        gv.cosmos.trackPointPositionsByIndices(indices);

        setTimeout(updateNoteLabels, 50);
    }, [updateNoteLabels]);

    // ── Save annotation from modal ──────────────────────────
    const handleSaveAnnotation = useCallback(
        (nodeId, { color, note }) => {
            setNodeAnnotations(prev => {
                const next = new Map(prev);
                if (!color && !note) {
                    next.delete(nodeId);
                } else {
                    next.set(nodeId, { color: color || null, note: note || '' });
                }
                nodeAnnotationsRef.current = next;

                // Sync annotations to URL
                const pairs = getAddressPairsFromUrl();
                const qs = buildUrlQs(pairs, next);
                window.history.replaceState({}, '', `${window.location.pathname}?${qs}`);

                return next;
            });

            // Apply color if not in heatmap mode
            if (color && !heatmapOn) {
                const gv = graphRef.current;
                if (gv) {
                    originalColors.current.set(nodeId, color);
                    const nodeColors = new Map();
                    nodeColors.set(nodeId, color);
                    gv.recolor(nodeColors, new Map());
                }
            }

            setTimeout(retrackAnnotatedNodes, 100);
        },
        [heatmapOn, retrackAnnotatedNodes],
    );

    // ── Core: load transactions for one address & push into graph ──
    const expandAddress = useCallback(
        async (address, network, parentId = null) => {
            const friendlyAddress = toFriendly(address, network);
            const key = `${network}:${friendlyAddress}`.toLowerCase();
            if (loadedAddrs.current.has(key)) {
                return;
            }
            loadedAddrs.current.add(key);

            const raw  = await fetchTransactionsForAddress(address, network);
            const norm = normalizeTxArray(raw);

            // Convert all from/to addresses to friendly format
            for (const tx of norm) {
                tx.from = toFriendly(tx.from, network);
                tx.to   = toFriendly(tx.to, network);
            }

            setAllTxBuckets(prev => [...prev, { address: friendlyAddress, network, transactions: norm }]);

            const whiteId = toFriendly(initial[0]?.address, initial[0]?.network) ?? friendlyAddress;
            const friendlyParent = parentId ? toFriendly(parentId, network) : null;
            const { nodes, links } = processTransactions(norm, whiteId, { labelFormatter });

            if (!nodes.length) {
                return;
            }

            const gv = graphRef.current;
            if (!gv) {
                return;
            }

            for (const n of nodes) {
                if (!originalColors.current.has(n.id)) {
                    const ann = nodeAnnotationsRef.current.get(n.id);
                    originalColors.current.set(n.id, ann?.color || n.color);
                }
            }

            if (friendlyParent) {
                gv.addNodes(nodes, links, { parentId: friendlyParent });
            } else {
                gv.addNodes(nodes, links);
            }

            // Apply annotation colors to newly added nodes
            const annColors = new Map();
            for (const n of nodes) {
                const ann = nodeAnnotationsRef.current.get(n.id);
                if (ann?.color) {
                    annColors.set(n.id, ann.color);
                }
            }
            if (annColors.size > 0) {
                gv.recolor(annColors, new Map());
            }

            const curParams = new URLSearchParams(window.location.search);
            const curAddrs = curParams.getAll('address');
            if (!curAddrs.some(a => a.toLowerCase() === friendlyAddress.toLowerCase())) {
                const curNetworks = curParams.getAll('network');
                const pairs = curAddrs.map((a, i) => ({
                    address: a,
                    network: curNetworks[i] || 'Ethereum',
                }));
                pairs.push({ address: friendlyAddress, network });
                const qs = buildUrlQs(pairs, nodeAnnotationsRef.current);
                window.history.replaceState({}, '', `${window.location.pathname}?${qs}`);
            }

            if (loadedAddrs.current.size === initial.length) {
                setTimeout(() => {
                    if (gv.cosmos?.fitView) {
                        gv.cosmos.fitView(500);
                    }
                }, 300);
            }

            setTimeout(retrackAnnotatedNodes, 500);
        },
        [initial, labelFormatter, retrackAnnotatedNodes],
    );

    // ── Initial load ────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;

        async function loadAll() {
            setLoading(true);
            setError(null);
            try {
                for (let i = 0; i < initial.length; i++) {
                    if (cancelled) {
                        return;
                    }
                    // Small delay between requests to stay under API rate limits
                    if (i > 0) {
                        await new Promise(r => setTimeout(r, 350));
                    }
                    await expandAddress(initial[i].address, initial[i].network);
                }
            } catch (e) {
                console.error(e);
                if (!cancelled) {
                    setError('Error fetching data.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        if (initial.length) {
            loadAll();
        } else {
            setLoading(false);
        }

        return () => {
            cancelled = true;
        };
    }, [initial, expandAddress]);

    // ── Click handling: single = modal, double = expand ─────
    const handleSingleClick = useCallback((nodeId) => {
        setSelectedAddress(nodeId);
        setIsModalOpen(true);
    }, []);

    const handleDoubleClick = useCallback(
        async (nodeId) => {
            setIsGraphLoading(true);

            const bucket = allTxBuckets.find(b =>
                (b.transactions || []).some(
                    t => t.from?.toLowerCase() === nodeId.toLowerCase() ||
                         t.to?.toLowerCase()   === nodeId.toLowerCase(),
                ),
            );
            const network = bucket?.network || initial[0]?.network || 'Ethereum';

            await expandAddress(nodeId, network, nodeId);
            setIsGraphLoading(false);
        },
        [allTxBuckets, initial, expandAddress],
    );

    const onPointClick = useCallback(
        (index) => {
            const gv = graphRef.current;
            if (!gv) {
                return;
            }
            const nodeId = gv.getIdByIndex(index);
            if (!nodeId) {
                return;
            }

            if (clickTimer.current.has(nodeId)) {
                clearTimeout(clickTimer.current.get(nodeId));
                clickTimer.current.delete(nodeId);
                handleDoubleClick(nodeId);
            } else {
                const timer = setTimeout(() => {
                    clickTimer.current.delete(nodeId);
                    handleSingleClick(nodeId);
                }, DOUBLE_CLICK_DELAY);
                clickTimer.current.set(nodeId, timer);
            }
        },
        [handleSingleClick, handleDoubleClick],
    );

    // ── Simulation toggle ───────────────────────────────────
    const [isAnimating, setIsAnimating] = useState(true);

    const toggleAnimation = useCallback(() => {
        const cosmos = graphRef.current?.cosmos;
        if (!cosmos) {
            return;
        }
        if (cosmos.isSimulationRunning) {
            cosmos.pause();
            setIsAnimating(false);
        } else {
            if (cosmos.progress === 1) {
                cosmos.start();
            } else {
                cosmos.unpause();
            }
            setIsAnimating(true);
        }
    }, []);

    // ── Tooltip on hover ────────────────────────────────────
    const onPointMouseOver = useCallback(
        (index, position) => {
            const gv = graphRef.current;
            if (!gv) {
                return;
            }
            const nodeId = gv.getIdByIndex(index);
            if (!nodeId) {
                return;
            }

            let network = 'Unknown';
            let txCount = 0;
            let volume  = 0;

            for (const bucket of allTxBucketsRef.current) {
                for (const t of (bucket.transactions || [])) {
                    const isFrom = t.from?.toLowerCase() === nodeId.toLowerCase();
                    const isTo   = t.to?.toLowerCase()   === nodeId.toLowerCase();
                    if (isFrom || isTo) {
                        network = bucket.network;
                        txCount += 1;
                        volume  += Number(t.valueNorm ?? 0);
                    }
                }
            }

            const cosmos = gv.cosmos;
            let sx = 0;
            let sy = 0;
            if (cosmos && position) {
                const screenPos = cosmos.spaceToScreenPosition(position);
                sx = screenPos[0];
                sy = screenPos[1];
            }

            const label = labelFormatter(nodeId, network);

            setTooltip({
                x: sx + 12,
                y: sy - 8,
                id: nodeId,
                label,
                network,
                txCount,
                volume: volume.toFixed(4),
            });
        },
        [labelFormatter],
    );

    const onPointMouseOut = useCallback(() => {
        setTooltip(null);
    }, []);

    // ── Suppress native dblclick zoom on canvas ─────────────
    useEffect(() => {
        const wrap = graphWrapRef.current;
        if (!wrap) {
            return;
        }
        const suppress = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };
        wrap.addEventListener('dblclick', suppress, true);
        return () => {
            wrap.removeEventListener('dblclick', suppress, true);
        };
    }, [loading]);

    // ── Heatmap: recolor by degree ──────────────────────────
    const applyHeatmap = useCallback(() => {
        const gv = graphRef.current;
        if (!gv) {
            return;
        }
        const allNodes = gv.getAllNodes();
        const allLinks = gv.manager?.links || [];

        if (!allNodes.length) {
            return;
        }

        const degree = new Map();
        for (const link of allLinks) {
            const s = link.source;
            const t = link.target;
            degree.set(s, (degree.get(s) || 0) + 1);
            degree.set(t, (degree.get(t) || 0) + 1);
        }

        const maxDeg = Math.max(1, ...degree.values());

        const nodeColors = new Map();
        for (const node of allNodes) {
            const d = degree.get(node.id) || 0;
            const ratio = Math.log1p(d) / Math.log1p(maxDeg);
            nodeColors.set(node.id, heatColor(ratio));
        }

        gv.recolor(nodeColors, new Map());
    }, []);

    const restoreColors = useCallback(() => {
        const gv = graphRef.current;
        if (!gv) {
            return;
        }
        const allNodes = gv.getAllNodes();
        const nodeColors = new Map();
        for (const node of allNodes) {
            const ann = nodeAnnotationsRef.current.get(node.id);
            const orig = ann?.color || originalColors.current.get(node.id) || node.color;
            nodeColors.set(node.id, orig);
        }

        // Reset link colors back to default
        const linkColors = new Map();
        const allLinks = gv.manager?.links || [];
        for (const link of allLinks) {
            const key = `${link.source}→${link.target}`;
            linkColors.set(key, '#1c2733');
        }

        gv.recolor(nodeColors, linkColors);
    }, []);

    const toggleHeatmap = useCallback(() => {
        setHeatmapOn(prev => {
            const next = !prev;
            if (next) {
                applyHeatmap();
            } else {
                restoreColors();
            }
            return next;
        });
    }, [applyHeatmap, restoreColors]);

    useEffect(() => {
        if (heatmapOn) {
            const t = setTimeout(applyHeatmap, 200);
            return () => clearTimeout(t);
        }
    }, [allTxBuckets, heatmapOn, applyHeatmap]);

    // ── Timeline: filter graph by time range ────────────────
    const onTimeRangeChange = useCallback(
        (rangeMs) => {
            const gv = graphRef.current;
            if (!gv) {
                return;
            }

            const allNodes = gv.getAllNodes();
            if (!allNodes.length) {
                return;
            }

            if (!rangeMs) {
                if (heatmapOn) {
                    applyHeatmap();
                } else {
                    restoreColors();
                }
                return;
            }

            const [startMs, endMs] = rangeMs;

            const activeNodes = new Set();
            for (const bucket of allTxBuckets) {
                for (const t of (bucket.transactions || [])) {
                    if (t.time instanceof Date) {
                        const ms = t.time.getTime();
                        if (ms >= startMs && ms <= endMs) {
                            if (t.from) {
                                activeNodes.add(t.from.toLowerCase());
                            }
                            if (t.to) {
                                activeNodes.add(t.to.toLowerCase());
                            }
                        }
                    }
                }
            }

            const nodeColors = new Map();
            for (const node of allNodes) {
                const isActive = activeNodes.has(node.id.toLowerCase());
                if (isActive) {
                    if (heatmapOn) {
                        nodeColors.set(node.id, null);
                    } else {
                        const ann = nodeAnnotationsRef.current.get(node.id);
                        nodeColors.set(node.id, ann?.color || originalColors.current.get(node.id) || node.color);
                    }
                } else {
                    nodeColors.set(node.id, '#0f181f');
                }
            }

            if (heatmapOn) {
                const allLinks = gv.manager?.links || [];
                const degree = new Map();
                for (const link of allLinks) {
                    if (activeNodes.has(link.source.toLowerCase()) && activeNodes.has(link.target.toLowerCase())) {
                        degree.set(link.source, (degree.get(link.source) || 0) + 1);
                        degree.set(link.target, (degree.get(link.target) || 0) + 1);
                    }
                }
                const maxDeg = Math.max(1, ...degree.values());
                for (const node of allNodes) {
                    if (activeNodes.has(node.id.toLowerCase())) {
                        const d = degree.get(node.id) || 0;
                        const ratio = Math.log1p(d) / Math.log1p(maxDeg);
                        nodeColors.set(node.id, heatColor(ratio));
                    }
                }
            }

            const linkColors = new Map();
            const allLinks = gv.manager?.links || [];
            for (const link of allLinks) {
                const key = `${link.source}→${link.target}`;
                const bothActive =
                    activeNodes.has(link.source.toLowerCase()) &&
                    activeNodes.has(link.target.toLowerCase());
                linkColors.set(key, bothActive ? '#243645' : '#0b1116');
            }

            gv.recolor(nodeColors, linkColors);
        },
        [allTxBuckets, heatmapOn, applyHeatmap, restoreColors],
    );

    // ── Load transactions from Navbar modal ─────────────────
    const loadNewTransactions = useCallback(
        async (address, network) => {
            setIsGraphLoading(true);
            await expandAddress(address, network, address);
            setIsGraphLoading(false);
        },
        [expandAddress],
    );

    // ── Cosmos config ───────────────────────────────────────
    const cosmosConfig = useMemo(
        () => ({
            backgroundColor: '#0b1116',
            spaceSize: 8192,
            simulationFriction: 0.85,
            simulationLinkSpring: 0.1,
            simulationLinkDistance: 15,
            simulationRepulsion: 5.0,
            simulationGravity: 0.01,
            simulationDecay: 8000,
            scalePointsOnZoom: true,
            scaleLinksOnZoom: true,
            linkWidthScale: 0.5,
            linkDefaultArrows: true,
            linkArrowsSizeScale: 1,
            curvedLinks: true,
            renderHoveredPointRing: true,
            hoveredPointRingColor: '#00d4ff',
            focusedPointRingColor: '#7a4dff',
            enableDrag: true,
            fitViewOnInit: true,
            fitViewDelay: 800,
            onPointClick,
            onPointMouseOver,
            onPointMouseOut,
            onSimulationTick: updateNoteLabels,
            onZoom: updateNoteLabels,
            onBackgroundClick: () => {
                if (graphRef.current?.cosmos?.unselectPoints) {
                    graphRef.current.cosmos.unselectPoints();
                }
            },
        }),
        [onPointClick, onPointMouseOver, onPointMouseOut, updateNoteLabels],
    );

    // ── Render ──────────────────────────────────────────────
    return (
        <div className="container">
            <Navbar
                selectedAddress={selectedAddress}
                allTransactions={allTxBuckets}
                loadNewTransactions={loadNewTransactions}
                toggleAnimation={toggleAnimation}
                isAnimating={isAnimating}
                toggleHeatmap={toggleHeatmap}
                heatmapOn={heatmapOn}
                setIsGraphLoading={setIsGraphLoading}
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                nodeAnnotations={nodeAnnotations}
                onSaveAnnotation={handleSaveAnnotation}
            />

            {error && <p className="error-msg">{error}</p>}

            <div className="hero-container">
                <div className="graph-wrap" ref={graphWrapRef}>
                    <GraphView
                        ref={graphRef}
                        cosmosConfig={cosmosConfig}
                        style={{ width: '100%', height: '100%' }}
                    />

                    {/* Floating note labels (DOM-managed, not React) */}
                    <div className="note-labels-overlay" ref={noteOverlayRef} />
                </div>

                {tooltip && (
                    <div
                        className="node-tooltip"
                        style={{ left: tooltip.x, top: tooltip.y }}
                    >
                        <div className="tt-label">{tooltip.label}</div>
                        <div className="tt-row">
                            <span className="tt-key">Network:</span>
                            <span>{tooltip.network}</span>
                        </div>
                        <div className="tt-row">
                            <span className="tt-key">Transactions:</span>
                            <span>{tooltip.txCount}</span>
                        </div>
                        <div className="tt-row">
                            <span className="tt-key">Volume:</span>
                            <span>{tooltip.volume}</span>
                        </div>
                    </div>
                )}

                <Timeline
                    txBuckets={allTxBuckets}
                    onRangeChange={onTimeRangeChange}
                />

                {(loading || isGraphLoading) && <Loading />}
            </div>
        </div>
    );
}